import React, { useState, useRef } from 'react';
import './Paint1.css';

const shapes = {
    FREEHAND: 'freehand',
    LINE: 'line',
    RECTANGLE: 'rectangle',
    SQUARE: 'square',
    CIRCLE: 'circle',
    TRIANGLE: 'triangle'
};

function Paint1() {
    const [drawing, setDrawing] = useState(false);
    const [brushColor, setBrushColor] = useState('black');
    const [fillColor, setFillColor] = useState('transparent');
    const [brushSize, setBrushSize] = useState(5);
    const [shape, setShape] = useState(shapes.FREEHAND);
    const [mode, setMode] = useState('desktop');
    const canvasRef = useRef(null);
    const startPos = useRef({ x: 0, y: 0 });
    const currentPos = useRef({ x: 0, y: 0 });

    const getOffset = (e) => {
        const canvas = canvasRef.current;
        if (mode === 'mobile') {
            const touch = e.touches[0] || e.changedTouches[0];
            const rect = canvas.getBoundingClientRect();
            return {
                offsetX: touch.clientX - rect.left,
                offsetY: touch.clientY - rect.top
            };
        } else {
            return {
                offsetX: e.nativeEvent.offsetX,
                offsetY: e.nativeEvent.offsetY
            };
        }
    };

    const startDrawing = (e) => {
        const { offsetX, offsetY } = getOffset(e);
        startPos.current = { x: offsetX, y: offsetY };
        setDrawing(true);
        if (shape === shapes.FREEHAND) draw(e);
    };

    const endDrawing = (e) => {
        if (!drawing) return;
        setDrawing(false);
        const { offsetX, offsetY } = getOffset(e);
        currentPos.current = { x: offsetX, y: offsetY };
        const ctx = canvasRef.current.getContext('2d');
        if (shape !== shapes.FREEHAND) {
            drawShape(ctx, startPos.current, currentPos.current);
        }
        ctx.beginPath();
    };

    const draw = (e) => {
        if (!drawing) return;
        const { offsetX, offsetY } = getOffset(e);
        currentPos.current = { x: offsetX, y: offsetY };
        const ctx = canvasRef.current.getContext('2d');
        ctx.lineWidth = brushSize;
        ctx.lineCap = 'round';
        ctx.strokeStyle = brushColor;

        if (shape === shapes.FREEHAND) {
            ctx.lineTo(offsetX, offsetY);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(offsetX, offsetY);
        }
    };

    const drawShape = (ctx, startPos, endPos) => {
        ctx.strokeStyle = brushColor;
        ctx.fillStyle = fillColor;
        switch (shape) {
            case shapes.LINE:
                ctx.beginPath();
                ctx.moveTo(startPos.x, startPos.y);
                ctx.lineTo(endPos.x, endPos.y);
                ctx.stroke();
                break;
            case shapes.RECTANGLE:
                const width = endPos.x - startPos.x;
                const height = endPos.y - startPos.y;
                ctx.fillRect(startPos.x, startPos.y, width, height);
                ctx.strokeRect(startPos.x, startPos.y, width, height);
                break;
            case shapes.SQUARE:
                const size = Math.min(Math.abs(endPos.x - startPos.x), Math.abs(endPos.y - startPos.y));
                ctx.fillRect(startPos.x, startPos.y, size, size);
                ctx.strokeRect(startPos.x, startPos.y, size, size);
                break;
            case shapes.CIRCLE:
                const radius = Math.sqrt(Math.pow(endPos.x - startPos.x, 2) + Math.pow(endPos.y - startPos.y, 2));
                ctx.beginPath();
                ctx.arc(startPos.x, startPos.y, radius, 0, 2 * Math.PI);
                ctx.fill();
                ctx.stroke();
                break;
            case shapes.TRIANGLE:
                ctx.beginPath();
                ctx.moveTo(startPos.x, startPos.y);
                ctx.lineTo(endPos.x, endPos.y);
                ctx.lineTo(startPos.x + (endPos.x - startPos.x) / 2, startPos.y - Math.abs(endPos.x - startPos.x));
                ctx.closePath();
                ctx.fill();
                ctx.stroke();
                break;
            default:
                break;
        }
    };

    const savePainting = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        // Create a new canvas to avoid altering the original
        const tempCanvas = document.createElement('canvas');
        const tempCtx = tempCanvas.getContext('2d');
        tempCanvas.width = canvas.width;
        tempCanvas.height = canvas.height;

        // Fill the background with white
        tempCtx.fillStyle = '#FFFFFF';
        tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

        // Draw the original canvas content onto the new canvas
        tempCtx.drawImage(canvas, 0, 0);

        // Save the new canvas as an image
        const link = document.createElement('a');
        link.download = 'painting.png';
        link.href = tempCanvas.toDataURL();
        link.click();
    };

    const clearCanvas = () => {
        const ctx = canvasRef.current.getContext('2d');
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    };

    return (
        <div className="paint1">
            <h1>Paint Here</h1>
            <div className="controls1">
                <label>Mode:</label>
                <input type="radio" id="desktop" name="mode" value="desktop" checked={mode === 'desktop'} onChange={(e) => setMode(e.target.value)} />
                <label htmlFor="desktop">Desktop</label>
                <input type="radio" id="mobile" name="mode" value="mobile" checked={mode === 'mobile'} onChange={(e) => setMode(e.target.value)} />
                <label htmlFor="mobile">Mobile</label>
            </div>
            <div className="controls1">
                <label>Brush Color: </label>
                <input type="color" value={brushColor} onChange={(e) => setBrushColor(e.target.value)} />
                <label>Fill Color: </label>
                <input type="color" value={fillColor} onChange={(e) => setFillColor(e.target.value)} />
                <label>Brush Size: </label>
                <input type="range" min="1" max="20" value={brushSize} onChange={(e) => setBrushSize(e.target.value)} />
                <label>Shape: </label>
                <select value={shape} onChange={(e) => setShape(e.target.value)}>
                    <option value={shapes.FREEHAND}>Freehand</option>
                    <option value={shapes.LINE}>Line</option>
                    <option value={shapes.RECTANGLE}>Rectangle</option>
                    <option value={shapes.SQUARE}>Square</option>
                    <option value={shapes.CIRCLE}>Circle</option>
                    <option value={shapes.TRIANGLE}>Triangle</option>
                </select>
                <button onClick={savePainting}>Save</button>
                <button onClick={clearCanvas}>Clear</button>
            </div>
            <canvas
                ref={canvasRef}
                onMouseDown={mode === 'desktop' ? startDrawing : null}
                onMouseUp={mode === 'desktop' ? endDrawing : null}
                onMouseMove={mode === 'desktop' ? draw : null}
                onTouchStart={mode === 'mobile' ? startDrawing : null}
                onTouchMove={mode === 'mobile' ? draw : null}
                onTouchEnd={mode === 'mobile' ? endDrawing : null}
                width={mode === 'mobile' ? window.innerWidth - 40 : 500}
                height={mode === 'mobile' ? window.innerHeight - 140 : 500}
                className="canvas"
            ></canvas>
        </div>
    );
}

export default Paint1;
